import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarDays, X } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { formatDateWithWeekday, parseLocalDate, toLocalYMD } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Calendar for picking WFH shifts beyond the 2-week quick list; loads one month at a time.
export const WFHLaterDatePicker = ({ userId, minDate, maxDate, selectedShifts, onToggle }) => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(minDate);
  const [shiftsByDate, setShiftsByDate] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    const from = new Date(month.getFullYear(), month.getMonth(), 1);
    const to = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    setLoading(true);
    axios.get(`${API}/users/${userId}/upcoming-shifts`, {
      params: { date_from: toLocalYMD(from < minDate ? minDate : from), date_to: toLocalYMD(to > maxDate ? maxDate : to) },
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(res => {
      const map = {};
      (res.data.shifts || []).forEach(s => { if (!map[s.date]) map[s.date] = s; });
      setShiftsByDate(prev => ({ ...prev, ...map }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open, month, userId]);

  const laterSelected = selectedShifts.filter(s => parseLocalDate(s.date) >= minDate);
  const hasShift = (day) => !!shiftsByDate[toLocalYMD(day)];

  return (
    <div className="mt-3" data-testid="wfh-later-picker">
      {!open ? (
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="wfh-later-picker-toggle">
          <CalendarDays className="h-4 w-4 mr-2" /> Pick a later date
        </Button>
      ) : (
        <div className="border rounded-lg p-2 bg-white">
          <div className="flex items-center justify-between px-2">
            <p className="text-xs text-gray-600">Days with a scheduled shift can be selected{loading ? ' — loading…' : ''}</p>
            <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600" data-testid="wfh-later-picker-close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex justify-center">
            <Calendar
              mode="multiple"
              month={month}
              onMonthChange={setMonth}
              fromDate={minDate}
              toDate={maxDate}
              selected={laterSelected.map(s => parseLocalDate(s.date))}
              disabled={(day) => !hasShift(day)}
              modifiers={{ hasShift }}
              modifiersClassNames={{ hasShift: 'font-semibold text-teal-700 underline decoration-teal-400' }}
              onDayClick={(day, mods) => { if (!mods.disabled) onToggle(shiftsByDate[toLocalYMD(day)]); }}
            />
          </div>
        </div>
      )}
      {laterSelected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="wfh-later-selected-list">
          {laterSelected.sort((a, b) => a.date.localeCompare(b.date)).map(s => (
            <span key={s.date} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">
              {formatDateWithWeekday(s.date)}
              <button type="button" onClick={() => onToggle(s)} data-testid={`wfh-later-remove-${s.date}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
